import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { twMerge } from "tailwind-merge";
import toast, { Toaster } from "react-hot-toast";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "components/ui/select";
import ConsoleTable, { DataListProps } from "../-components/ConsoleTable";
import Pagination from "components/Pagination";
import PageHeader from "../-components/PageHeader";
import ConfirmDialog from "components/popup/ConfirmDialog";
import Seo from "components/Seo";
import { makeIntComma, toastErrorSyle } from "hooks/utils/util";
import { useGetConsoleDrivers, useDeleteConsoleDriver } from "hooks/console/useConsoleDriver";
import { ReactComponent as ArrowDown } from "assets/morning/icon/arrowDownNavy.svg";
import { ReactComponent as ReadingGlasses } from "assets/common/common/readingGlasses.svg";

const theadStyle = "text-[#222222] text-[16px] text-center";

export default function ConsoleDrivers() {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [selectedRows, setSelectedRows] = useState<number[]>([]);
    const [searchForm, setSearchForm] = useState({ search: "name", searchtxt: "", page: 1, limit: "10" });
    const allCheckRef = useRef<HTMLInputElement | null>(null);
    const { data, refetch, isLoading } = useGetConsoleDrivers(searchForm);
    const { mutate: deleteDriver } = useDeleteConsoleDriver();

    useEffect(() => {
        const page = searchParams.get("page") ?? "1";
        setSearchForm(prev => ({ ...prev, page: Number(page) }));
    }, [searchParams]);

    const deleteRow = (close: () => void) => {
        deleteDriver(
            { driver_no: selectedRows.map(Number) },
            {
                onSuccess: () => {
                    toast.success("성공적으로 삭제 되었습니다.");
                    close();
                    setSelectedRows([]);
                    refetch();
                },
                onError: (error: any) => toast.error(error.message, toastErrorSyle),
            },
        );
    };

    const toggleRow = (id: number) => {
        const allIdxs = data?.data_list.map(item => item.driver_no);
        setSelectedRows(prev => {
            if (prev.includes(id)) {
                const newRows = prev.filter(rowId => rowId !== id);
                if (newRows.length !== allIdxs?.length) allCheckRef.current?.click();
                return newRows;
            }
            const newRows = [...prev, id];
            if (newRows.length === allIdxs?.length) allCheckRef.current?.click();
            return newRows;
        });
    };

    const allCheck = (checked: boolean) => {
        const allIdxs = data?.data_list.map(item => item.driver_no);
        setSelectedRows(checked && allIdxs ? allIdxs : []);
    };

    return (
        <>
            <Seo type="console" pageTitle="기사 관리" />
            <div className="flex-1 flex flex-col overflow-hidden max-h-[100dvh]">
                <PageHeader category="고객서비스" title="기사 관리" />
                <div className="flex-1 bg-[#F7F6FB] flex overflow-hidden">
                    <div className="px-[40px] py-[28px] w-full overflow-y-auto">
                        <div className="bg-white rounded-[20px] py-[36px] px-[40px] w-full min-w-[900px]">
                            <form onSubmit={e => e.preventDefault()}>
                                <div className="flex items-center gap-2 justify-end">
                                    <Select
                                        value={searchForm.limit}
                                        onValueChange={value => setSearchForm(prev => ({ ...prev, limit: value }))}
                                    >
                                        <SelectTrigger className="relative shadow-none text-[#1B1C1E] text-[16px] flex gap-[10px] rounded-[5px] focus:ring-2 max-w-[150px] border border-[#E1E2E4] bg-white h-[50px]">
                                            <SelectValue placeholder="" />
                                            <ArrowDown className="[&_path]:fill-[#666666]" />
                                        </SelectTrigger>
                                        <SelectContent className="bg-white">
                                            <SelectItem value="20">20개</SelectItem>
                                            <SelectItem value="10">10개</SelectItem>
                                        </SelectContent>
                                    </Select>
                                    <div className="flex items-center border border-[#dddddd] rounded-[5px] bg-white overflow-hidden w-[320px] px-[16px]">
                                        <input
                                            type="text"
                                            value={searchForm.searchtxt}
                                            onChange={e => setSearchForm(prev => ({ ...prev, searchtxt: e.target.value }))}
                                            placeholder="기사명을 입력해주세요."
                                            className="text-[16px] placeholder:text-[#878A93] w-full focus:outline-none py-[12px]"
                                        />
                                        <ReadingGlasses />
                                    </div>
                                </div>
                            </form>

                            <div className="mt-[8px] mb-[40px]">
                                <div className="bg-[#DCE1EC] flex justify-between px-[30px] py-[20px] rounded-[5px]">
                                    <div className="flex items-center gap-[30px]">
                                        <label htmlFor="all" className="flex items-center gap-x-2">
                                            <input type="checkbox" id="all" onChange={e => allCheck(e.target.checked)} ref={allCheckRef} />
                                            <span>전체선택</span>
                                        </label>
                                        <label className="text-[#222222] text-[18px]">
                                            선택한 기사 총 <span className="text-[#4F46E5]">{selectedRows.length}</span>명
                                        </label>
                                    </div>
                                    <ConfirmDialog
                                        title="기사 삭제"
                                        trigger={open => (
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    if (selectedRows.length === 0) {
                                                        toast.error("삭제할 기사를 선택해주세요.", toastErrorSyle);
                                                        return;
                                                    }
                                                    open();
                                                }}
                                                className="bg-[#FF5463] text-white text-[14px] rounded-[5px] px-[12px] py-[7.5px]"
                                            >
                                                삭제
                                            </button>
                                        )}
                                        handleConfirm={deleteRow}
                                    />
                                </div>

                                <ConsoleTable
                                    thead={[
                                        {
                                            title: "NO",
                                            width: "10%",
                                            key: "index",
                                            content: (row: DataListProps, rowIndex) => {
                                                const index = (data?.total_count ?? 0) - (searchForm.page - 1) * Number(searchForm.limit) - rowIndex;
                                                return (
                                                    <label
                                                        className="flex items-center justify-center gap-x-1 px-2 flex-wrap"
                                                        onClick={e => e.stopPropagation()}
                                                    >
                                                        <input
                                                            type="checkbox"
                                                            checked={selectedRows.includes(row.driver_no)}
                                                            onChange={() => toggleRow(row.driver_no)}
                                                        />
                                                        <p className="break-all text-center">{makeIntComma(index) ?? 0}</p>
                                                    </label>
                                                );
                                            },
                                        },
                                        { title: "기사명", width: "30%", key: "name", className: twMerge(theadStyle, "text-[#666666]") },
                                        { title: "전화번호", width: "30%", key: "phone", className: twMerge(theadStyle, "text-[#666666]") },
                                        { title: "차량번호", width: "30%", key: "car_no", className: twMerge(theadStyle, "text-[#666666]") },
                                    ]}
                                    tbody={data?.data_list || []}
                                    uniqueKeyName="driver_no"
                                    isLoading={isLoading}
                                    trClassName="hover:bg-[#f8f8f8] cursor-pointer"
                                    trClick={row => navigate(`${row.driver_no}`, { state: { page: searchParams.get("page") ?? "1" } })}
                                />

                                <div className="flex justify-end mt-5">
                                    <button
                                        className="bg-black text-white rounded-[5px] px-4 py-2"
                                        onClick={() => navigate("create", { state: { page: searchParams.get("page") ?? "1" } })}
                                    >
                                        작성하기
                                    </button>
                                </div>
                            </div>

                            <Pagination
                                current={searchForm.page}
                                total={data?.total_count || 1}
                                pageSize={Number(searchForm.limit)}
                                onChange={newPage => {
                                    searchParams.set("page", String(newPage));
                                    setSearchParams(searchParams);
                                }}
                            />
                        </div>
                    </div>
                </div>
            </div>
            <Toaster position="bottom-center" reverseOrder={false} />
        </>
    );
}
